import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import {
  Add as AddIcon,
  Approval as ApprovalIcon,
  CallSplit as CallSplitIcon,
  ChevronRight as ChevronRightIcon,
  FitScreen as FitScreenIcon,
  FlagCircle as FlagCircleIcon,
  Group as CcIcon,
  Notifications as NotificationsIcon,
  PlayArrow as PlayArrowIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  approvalTargetHours,
  formatApprovalTargetHours,
} from '../../pages/Processes/approvalTarget';
import { flowNodeHasDetails } from '../../pages/Processes/processDisplay';
import ReactFlow, {
  Background,
  BackgroundVariant,
  BaseEdge,
  ControlButton,
  Controls,
  Edge,
  type EdgeProps,
  Handle,
  MarkerType,
  Node,
  NodeProps,
  Position,
  ReactFlowInstance,
  ReactFlowProvider,
  getSmoothStepPath,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';

export interface FlowNode {
  id: string;
  type: string;
  position?: { x: number; y: number };
  data?: { label?: string; config?: Record<string, any> };
}
export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string | null;
  label?: string | null;
  data?: { on?: string | null };
}
interface FlowTimelineEvent {
  action: string;
  node_id?: string | null;
  outcome?: string | null;
}
interface FlowViewerProps {
  flow: { nodes?: FlowNode[]; edges?: FlowEdge[] } | null;
  currentNodeId: string | null;
  status: string;
  timeline?: FlowTimelineEvent[] | null;
  currentAssignee?: { worker_id: string; name: string; email?: string | null } | null;
  /**
   * WHO can act on the CURRENT step, by name, when no single person is resolved.
   *
   * Added 2026-09-09 (owner, production, IMG_4074). A role step leaves `current_approver_id` null by
   * design, so this panel could only render the routing RULE — `Role: Process Owner` — and the reader
   * had no way to tell who to chase. Server-supplied
   * (`ProcessApproverDescriptor.candidates`), sourced from the pending task's `eligible_approver_ids`,
   * which is the same list `engine._assert_can_action` enforces — so these names are exactly the people
   * who can decide, and cannot drift from the guard.
   *
   * Phone twin: `svdy-mobile/src/components/ApprovalFlow.tsx` — update in lockstep (svdy-ui §13).
   */
  approverCandidates?: { worker_id: string; name: string }[] | null;
  /** Localizes a seeded built-in's node label. See `flowNodeLabel`. */
  resolveNodeLabel?: (node: FlowNode) => string | undefined;
}

export type FlowNodeState = 'completed' | 'current' | 'stopped' | 'notReached';

const NODE_WIDTH = 184;
/**
 * The height of a node's HEADER — icon, label, state — and nothing else.
 *
 * This replaces the former `NODE_HEIGHT = 66`, and the rename is the point: **a node is no longer a
 * fixed-height box.** A node with named exits carries one `OUTPUT_ROW_HEIGHT` row per exit below the
 * header, so its total height is `flowNodeHeight()` and the old constant could only ever describe a
 * node with no branches. See the retirement note above `layoutFlowHorizontally`.
 *
 * The component reads THIS CONSTANT for the header's `height` rather than repeating `66`, so the box
 * and the model that measures it cannot drift apart — which the old pair (`const NODE_HEIGHT = 66`
 * plus `minHeight: 66` in the JSX) could only be checked for by asserting two literals agreed.
 */
export const NODE_HEADER_HEIGHT = 66;
/**
 * One named-output row. `fontSize: 0.66rem` on a 600 weight measures ~11px of ink, so 22 gives it the
 * same optical breathing room the state caption has in the header, and two rows (the common
 * approve/reject pair) add 44px — inside the 38px lane corridor plus the clearance the stacker adds.
 */
export const OUTPUT_ROW_HEIGHT = 22;
/**
 * Horizontal distance between rank ORIGINS, so the gap between node EDGES is `RANK_GAP - NODE_WIDTH`.
 *
 * **RE-DERIVED 2026-09-10, back to the pre-label value.** Round 26 sized this from the branch LABEL
 * widths (236 → 300) because a label sat centred in that gap and Arabic `تمت الموافقة` (~66px) did not
 * fit in 52px. That arithmetic was correct for as long as the label lived on the EDGE. It does not any
 * more: the label is a row inside the source node (`flowNodeOutputs`), so the gap constrains nothing
 * but the readability of a line and its arrowhead, and 236 − 184 = 52px is what this viewer used for
 * that before the labels were ever moved into it.
 *
 * Keeping 300 would have left a constant whose stated reason had been retired — which is the seed of
 * the next drift (svdy-ui §17), and 64px per rank is real horizontal budget on a five-rank flow.
 *
 * The label measurement itself is NOT discarded. `flowViewerGeometry.test.ts` still derives the widest
 * translation from the locale files and now asserts it fits the OUTPUT ROW's inner width, which is the
 * box the label actually lives in.
 */
const RANK_GAP = 236;
/**
 * Vertical clearance between two sibling nodes in one rank, measured between their BOXES.
 *
 * Formerly `LANE_GAP = 104`, an origin-to-origin pitch that silently assumed every node was
 * `NODE_HEIGHT` tall — 104 − 66 = 38px of corridor. With variable heights a pitch is the wrong
 * primitive: a 110px node at lane 0 would reach 6px into lane 1's box. So the stacker adds heights and
 * inserts this clearance, which reproduces the old positions exactly when every node is 66 tall and
 * stays correct when they are not.
 */
const LANE_CLEARANCE = 38;
/**
 * `fitView` bounds. `maxZoom` is what decides how much of the box a SMALL graph fills.
 *
 * At 1.15 a five-step flow sat in the middle of a full-width canvas with wide empty margins (owner,
 * 2026-09-08: "半个画布空着"). 1.5 lets a short flow grow into its box while still capping the zoom, so a
 * three-node definition does not render as three billboards. A long flow is unaffected — it is bounded by
 * width, not by this cap.
 *
 * `minZoom` stays at 0.65 so a genuinely large flow shrinks to fit rather than overflowing.
 */
const FIT_VIEW_OPTIONS = { padding: 0.16, minZoom: 0.65, maxZoom: 1.5 };

const ICONS: Record<string, React.ReactNode> = {
  start: <PlayArrowIcon sx={{ fontSize: 17 }} />,
  end: <FlagCircleIcon sx={{ fontSize: 17 }} />,
  approval: <ApprovalIcon sx={{ fontSize: 17 }} />,
  notification: <NotificationsIcon sx={{ fontSize: 17 }} />,
  condition: <CallSplitIcon sx={{ fontSize: 17 }} />,
  cc: <CcIcon sx={{ fontSize: 17 }} />,
};

const STATE_COLOR: Record<FlowNodeState, string> = {
  completed: '#2e7d32',
  current: '#1565c0',
  stopped: '#c62828',
  notReached: '#757575',
};

/**
 * A node's label, LOCALIZED when the definition is one of the seeded built-ins.
 *
 * `resolve` is the localizer, supplied by the caller. Without it this returned the raw
 * `data.label` — which for a seeded flow is the English the seed wrote, so the runtime
 * instance view (the surface a worker and an approver actually read) showed `Submit` /
 * `Manager approval` / `Notify rejected result` while every status underneath them was
 * translated. `getProcessDefinitionNodeLabel` already existed and both Designer components
 * used it; this viewer did not. One adherent, one violation, and the violation was the
 * surface that matters (2026-08-26 audit).
 *
 * Passed in rather than imported so this module stays free of the Processes page tree —
 * `FlowViewer` is a shared component and the localizer needs the parent DEFINITION, which
 * only its caller has.
 */
export function flowNodeLabel(
  node: FlowNode,
  resolve?: (node: FlowNode) => string | undefined,
): string {
  const localized = resolve?.(node);
  if (localized) return localized;
  const config = node.data?.config ?? {};
  return String(
    node.data?.label ?? config.title ?? config.label ?? config.name ?? node.id,
  );
}

/**
 * A node's OUTPUTS — the named exits it offers, which is where a branch label now lives.
 *
 * WHY THE LABEL MOVED OFF THE EDGE (owner, 2026-09-09, §11.6 phrasing: *"重新用另一种新的方案做重构"*).
 * Three rounds were spent making an edge label fit: `RANK_GAP` 236 → 300 sized the gap from the label
 * widths, then `branchLabelSlot` solved a y-offset for the label of an edge that spans two ranks. Both
 * were correct and neither could ever be enough, because the ceiling is arithmetic:
 *
 *     gap between two ranks    RANK_GAP 300 - NODE_WIDTH 184 = 116px   -> fits ONE 78px label
 *     corridor between lanes   LANE_GAP  104 - NODE_HEIGHT  66 =  38px
 *     the owner's own flow      4 labelled edges, one of them spanning 2 ranks
 *
 * A corridor that holds one label cannot hold the labels of every condition that crosses it, so any
 * flow with more than one branch wants more chips in that space than it has. The primitive was wrong,
 * not the arithmetic.
 *
 * So the label is a ROW ON THE SOURCE NODE, with its own handle, and the edge carries no label at all.
 * There is then no such thing as an edge-label collision at any condition count.
 *
 * AND THE CONVENTION ALREADY EXISTED IN THIS REPO, one directory away. `FlowEditor.tsx` hit the identical
 * defect on 2026-08-23 — owner: *"approved和rejected字被线条挡住"* — and fixed it the same way, with a
 * comment that reads like a prediction of this one:
 *
 *     "The outcome names live INSIDE the card, on its bottom row. They used to sit at `bottom: -20`,
 *      centred on their handle — and the edge leaves that same handle heading straight down, so the line
 *      ran through the text."
 *
 * So this viewer was the violation, not the pioneer, and three rounds were spent re-deriving a fix that
 * was committed two weeks earlier in a sibling file (the same shape as this component's own
 * `getProcessDefinitionNodeLabel` finding: one adherent, one violation, and the violation was the surface
 * that matters). n8n / Camunda Modeler / Zapier / Retool all draw named outputs on the node too, but the
 * in-repo precedent is the deciding evidence.
 *
 * The ROW LAYOUT differs from the designer's because the surfaces differ (svdy-ui §20): the designer flows
 * top-down with its two handles at the card's bottom corners, so ONE row with `space-between` maps each
 * name to a corner. Here the handles stack down the node's right edge, so the mapping is one row per
 * handle, each name against the trailing edge beside its own dot.
 *
 * THE COST, stated rather than discovered later: a node's height is no longer a constant. `NODE_HEIGHT`
 * is gone, `branchLabelSlot` and its `LABEL_HALF_W`/`LABEL_HALF_H` box model are gone with it, and the
 * geometry assertions that pinned `NODE_HEIGHT === 66` retire in favour of the invariant that actually
 * matters now: no two node BOXES may overlap once heights vary. See `flowViewerGeometry.test.ts`, whose
 * header records the same retirement.
 */
export interface FlowOutput {
  /** The handle id React Flow attaches the edge to. Null means the node's single unnamed exit. */
  key: string | null;
  /** The branch name to render, or null for an unnamed default exit (which gets no row). */
  name: string | null;
}

const nonEmpty = (value?: string | null): string | null => {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? null : trimmed;
};

/**
 * The IDENTITY of an output — authored handle first, because that is what React Flow matches an edge
 * against. A snapshot that names its branch only in `data.on` or `label` (older definitions do) gets
 * that name promoted to a handle id, so two branches leaving one node cannot both fall through to the
 * node's first handle and draw from the same row.
 */
export function flowOutputKey(edge: FlowEdge): string | null {
  return nonEmpty(edge.sourceHandle) ?? nonEmpty(edge.data?.on) ?? nonEmpty(edge.label);
}

/**
 * The NAME an output shows — authored label first, because that is the human wording; the handle id is
 * the fallback. Deliberately the opposite precedence from `flowOutputKey`: one answers "which socket",
 * the other "what does the reader see".
 */
export function flowOutputName(edge: FlowEdge): string | null {
  return nonEmpty(edge.label) ?? nonEmpty(edge.sourceHandle) ?? nonEmpty(edge.data?.on);
}

/** The node's outputs in authored order, one per distinct handle. */
export function flowNodeOutputs(edges: FlowEdge[], nodeId: string): FlowOutput[] {
  const outputs: FlowOutput[] = [];
  for (const edge of edges) {
    if (edge.source !== nodeId) continue;
    const key = flowOutputKey(edge);
    if (outputs.some((existing) => existing.key === key)) continue;
    outputs.push({ key, name: flowOutputName(edge) });
  }
  return outputs;
}

/**
 * Total rendered height: the header, plus one row per NAMED output.
 *
 * An unnamed exit gets no row — a flow with no conditions is exactly as tall as it was before this
 * change, which is the majority case and the reason the corridor arithmetic still holds for it.
 */
export function flowNodeHeight(outputs: FlowOutput[]): number {
  const named = outputs.filter((output) => output.name).length;
  return NODE_HEADER_HEIGHT + named * OUTPUT_ROW_HEIGHT;
}

/**
 * The `top` offset, in px from the node's own top edge, for each output's handle — in the same order as
 * the outputs, so the caller can zip them.
 *
 * A named output's dot sits at the vertical centre of ITS OWN ROW, which is the whole point: the reader
 * sees `Rejected` and the line leaving beside it, with no chip anywhere on the canvas to collide.
 * Unnamed exits are spread down the header, which is what the old percentage layout did for every
 * handle.
 */
export function outputHandleOffsets(outputs: FlowOutput[]): number[] {
  const unnamed = outputs.filter((output) => !output.name).length;
  let namedSeen = 0;
  let unnamedSeen = 0;
  return outputs.map((output) => {
    if (output.name) {
      const row = namedSeen;
      namedSeen += 1;
      return NODE_HEADER_HEIGHT + row * OUTPUT_ROW_HEIGHT + OUTPUT_ROW_HEIGHT / 2;
    }
    const index = unnamedSeen;
    unnamedSeen += 1;
    return ((index + 1) / (unnamed + 1)) * NODE_HEADER_HEIGHT;
  });
}

/**
 * Viewer coordinates are deliberately independent from editor coordinates.
 * The editor needs 200px vertical drag room; the read-only viewer needs a
 * readable left-to-right story. Longest-path rank gives the horizontal stage,
 * while siblings in one rank occupy separate vertical lanes.
 *
 * Lanes are STACKED BY HEIGHT, not spaced by a fixed pitch — see `LANE_CLEARANCE`. `heights` is
 * returned so a caller (and the geometry test) can build the real boxes instead of assuming one.
 */
export function layoutFlowHorizontally(
  flow: { nodes?: FlowNode[]; edges?: FlowEdge[] } | null,
): { positions: Map<string, { x: number; y: number }>; heights: Map<string, number>; height: number } {
  const nodes = flow?.nodes ?? [];
  const edges = flow?.edges ?? [];
  const ids = new Set(nodes.map((node) => node.id));
  const indegree = new Map(nodes.map((node) => [node.id, 0]));
  const outgoing = new Map<string, FlowEdge[]>();
  for (const edge of edges) {
    if (!ids.has(edge.source) || !ids.has(edge.target)) continue;
    indegree.set(edge.target, (indegree.get(edge.target) ?? 0) + 1);
    outgoing.set(edge.source, [...(outgoing.get(edge.source) ?? []), edge]);
  }

  const rank = new Map<string, number>();
  const queue = nodes.filter((node) => (indegree.get(node.id) ?? 0) === 0);
  if (queue.length === 0 && nodes[0]) queue.push(nodes[0]);
  queue.forEach((node) => rank.set(node.id, 0));
  for (let i = 0; i < queue.length; i += 1) {
    const node = queue[i];
    for (const edge of outgoing.get(node.id) ?? []) {
      rank.set(edge.target, Math.max(rank.get(edge.target) ?? 0, (rank.get(node.id) ?? 0) + 1));
      indegree.set(edge.target, (indegree.get(edge.target) ?? 1) - 1);
      if (indegree.get(edge.target) === 0) {
        const target = nodes.find((candidate) => candidate.id === edge.target);
        if (target) queue.push(target);
      }
    }
  }
  // A malformed/cyclic snapshot still renders instead of disappearing.
  let fallbackRank = Math.max(0, ...Array.from(rank.values()));
  for (const node of nodes) {
    if (!rank.has(node.id)) rank.set(node.id, ++fallbackRank);
  }

  const heights = new Map<string, number>(
    nodes.map((node) => [node.id, flowNodeHeight(flowNodeOutputs(edges, node.id))]),
  );

  const byRank = new Map<number, FlowNode[]>();
  for (const node of nodes) {
    const r = rank.get(node.id) ?? 0;
    byRank.set(r, [...(byRank.get(r) ?? []), node]);
  }
  const positions = new Map<string, { x: number; y: number }>();
  for (const [r, rankNodes] of Array.from(byRank.entries())) {
    rankNodes.sort((a, b) =>
      (a.position?.x ?? 0) - (b.position?.x ?? 0) ||
      (a.position?.y ?? 0) - (b.position?.y ?? 0),
    );
    const lane = rankNodes.map((node) => heights.get(node.id) ?? NODE_HEADER_HEIGHT);
    const total = lane.reduce((sum, h) => sum + h, 0) + (rankNodes.length - 1) * LANE_CLEARANCE;
    let y = -total / 2;
    rankNodes.forEach((node, index) => {
      positions.set(node.id, { x: r * RANK_GAP, y });
      y += lane[index] + LANE_CLEARANCE;
    });
  }
  // The canvas must hold the tallest COLUMN, which with variable heights is no longer a lane count.
  const extent = nodes.length === 0 ? 0 : Math.max(
    ...nodes.map((node) => (positions.get(node.id)?.y ?? 0) + (heights.get(node.id) ?? 0)),
  ) - Math.min(...nodes.map((node) => positions.get(node.id)?.y ?? 0));
  return {
    positions,
    heights,
    // `extent + 170` reproduces the previous 236-at-one-lane / 340-at-two-lanes framing exactly when
    // every node is header-height, and grows with a node that carries output rows.
    height: Math.min(640, Math.max(320, extent + 170)),
  };
}

export function deriveFlowNodeState(
  nodeId: string,
  visited: Set<string>,
  currentNodeId: string | null,
  stoppedNodeId: string | null,
): FlowNodeState {
  if (nodeId === stoppedNodeId) return 'stopped';
  if (nodeId === currentNodeId) return 'current';
  if (visited.has(nodeId)) return 'completed';
  return 'notReached';
}

interface ViewerNodeData {
  label: string;
  nodeType: string;
  state: FlowNodeState;
  stateLabel: string;
  icon: React.ReactNode;
  selected: boolean;
  hasIncoming: boolean;
  /** Named exits become rows; an unnamed exit is a bare handle on the header. See `flowNodeOutputs`. */
  outputs: FlowOutput[];
  onSelect: () => void;
}

function FlowNodeComponent({ data }: NodeProps<ViewerNodeData>) {
  const color = STATE_COLOR[data.state];
  const offsets = outputHandleOffsets(data.outputs);
  const named = data.outputs.filter((output) => output.name);
  return (
    <Box
      // The `dir` ATTRIBUTE, not `direction` in sx: App.tsx's stylis RTL plugin rewrites the CSS
      // `direction: ltr` to `rtl` under Arabic, so the pin below never took effect (2026-10-21).
      dir="ltr"
      role="button"
      tabIndex={0}
      aria-label={[`${data.label}: ${data.stateLabel}`, ...named.map((output) => output.name)].join(' · ')}
      onClick={data.onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          data.onSelect();
        }
      }}
      sx={{
        width: NODE_WIDTH,
        // PHYSICAL left-to-right, in every locale including `ar`.
        //
        // This repo has a stylis RTL plugin, so logical properties and `flex-end` flip under Arabic —
        // but a React Flow canvas does not: nodes are placed by `transform: translate(x, y)`, the target
        // handle is on the physical left and the source handles on the physical right. Letting the
        // node's chrome mirror while its handles do not would put every output label at the opposite end
        // of the node from the line it names, which is the defect this whole restructure removes.
        // Arabic TEXT still renders right-to-left inside its own line box; only the layout is pinned.
        // Pinned by the `dir` attribute above — see there for why it cannot live in sx.
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        border: data.selected ? '2px solid' : '1px solid',
        borderColor: data.selected ? color : 'divider',
        borderRadius: 1,
        color: 'text.primary',
        cursor: 'pointer',
        boxShadow: data.state === 'current' ? `0 0 0 3px ${color}22` : 'none',
        transition: 'border-color 120ms, background-color 120ms',
        '&:hover, &:focus-visible': { borderColor: color, outline: 'none', bgcolor: 'action.hover' },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      {data.hasIncoming && (
        <Handle
          type="target"
          position={Position.Left}
          // The header's centre, not the node's: with output rows below, a 50% default would drop the
          // incoming dot beneath the header and away from the line that arrives at it.
          style={{ top: NODE_HEADER_HEIGHT / 2, width: 5, height: 5, border: 0, background: color }}
        />
      )}
      <Box sx={{ height: NODE_HEADER_HEIGHT, display: 'flex', alignItems: 'center', gap: 1, px: 1.25 }}>
        <Box sx={{ color, display: 'flex', flexShrink: 0 }}>{data.icon}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap title={data.label} sx={{ fontSize: '0.78rem', fontWeight: 650 }}>
            {data.label}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color, fontSize: '0.66rem', fontWeight: 600 }}>
            {data.stateLabel}
          </Typography>
        </Box>
      </Box>
      {named.map((output) => (
        <Box
          key={output.key ?? output.name}
          sx={{
            height: OUTPUT_ROW_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.25,
            px: 1.25,
            borderTop: '1px solid',
            borderColor: 'divider',
            color: 'text.secondary',
          }}
        >
          <Typography noWrap title={output.name ?? ''} sx={{ fontSize: '0.66rem', fontWeight: 600 }}>
            {output.name}
          </Typography>
          {/* An ICON, never a `▸` glyph: U+25B8 is bidi-neutral, so in Arabic it reorders to the other
              side of the label and points back at the node it leaves. The same trap cost two `LinkRow`
              labels a literal `→` on 2026-09-09. An MUI SVG is not mirrored, which is correct here —
              the graph itself is physically left-to-right. */}
          <ChevronRightIcon sx={{ fontSize: 13, flexShrink: 0 }} />
        </Box>
      ))}
      {data.outputs.map((output, index) => (
        <Handle
          key={output.key ?? `default-${index}`}
          id={output.key ?? undefined}
          type="source"
          position={Position.Right}
          style={{
            width: 5,
            height: 5,
            border: 0,
            background: color,
            // Px, from `outputHandleOffsets`: a named output's dot is the centre of its own row, so the
            // label and the line it names are the same row. A percentage cannot express that once the
            // node's height depends on how many rows it has.
            top: offsets[index],
          }}
        />
      ))}
    </Box>
  );
}

const nodeTypes = { flowNode: FlowNodeComponent };

/**
 * An edge whose VERTICAL SEGMENT is placed by us, so a two-rank edge routes AROUND the node between.
 *
 * THE DEFECT (owner, production, 2026-09-09, screenshot): the branch LABELS stopped colliding when they
 * became node rows — that part worked. The LINE still went through. `getSmoothStepPath` puts its vertical
 * run at the midpoint X, and for an edge spanning two ranks the midpoint X *is* the intervening rank, so
 * `Manager approval → Rejected` left its row, ran straight into `Process owner approval`'s box, and came
 * out the other side. Two grey lines read as one continuous line passing through the middle node, and the
 * reader cannot tell where a rejection actually goes. Exactly the geometry `branchLabelSlot` was fighting,
 * one layer down — the label was never the whole problem.
 *
 * `centerX` is a real parameter of `getSmoothStepPath`, read out of the installed
 * `@reactflow/core/.../SmoothStepEdge.d.ts` rather than assumed. Setting it to the middle of the gap
 * IMMEDIATELY AFTER the source makes the path drop into the lane while still between two ranks — a
 * corridor the lane stacker guarantees is empty — and then run horizontally in the TARGET's own lane,
 * which is where the target lives. That is what every graph editor does, and it needs no solver.
 *
 * NOT a revival of `BranchEdgeComponent`. That one existed to POSITION A LABEL, and it is retired because
 * there are no edge labels any more. This one exists to ROUTE A LINE, which is a layout responsibility a
 * left-to-right graph cannot delegate. Same file, opposite subject.
 */
export interface FlowBox { x1: number; x2: number; y1: number; y2: number }

/**
 * The waypoints a SKIP edge must follow to reach its target without crossing a card, or `undefined` when
 * the built-in smooth step is already clean.
 *
 * WHY `centerX` ALONE WAS NOT ENOUGH (owner, 2026-09-10, screenshot, after a 20-topology audit had
 * reported zero overlaps). `getSmoothStepPath` with an explicit `centerX` moves the VERTICAL turn into the
 * gap after the source — but the long horizontal run that follows still sits at `targetY`, and for a
 * two-rank edge that Y is inside the intervening card. Measured on the emitted path for the flow in the
 * screenshot: the run went `x=450 → 688` at `y=52` while `owner_approval` occupied `x=472..656,
 * y=-55..55`. Straight through it.
 *
 * So the horizontal run has to leave the band the cards occupy. The route is five points: out of the source
 * into the gap, down past everything it spans, across, up into the gap before the target, and in. Both
 * vertical runs sit in RANK GAPS, where no card can be, and the horizontal one sits below every card whose
 * x-range it crosses — so the result is clean by construction rather than by measurement.
 *
 * Clearance is derived per EDGE from the boxes it actually spans, not from the whole canvas: a short skip
 * then routes higher than a long one, which keeps two skips off each other without a lane allocator.
 */
export function skipEdgePoints(
  source: { x: number; y: number },
  target: { x: number; y: number },
  obstacles: FlowBox[],
): [number, number][] | undefined {
  if (target.x - source.x <= RANK_GAP + 1) return undefined;
  const spanned = obstacles.filter((b) => b.x2 > source.x && b.x1 < target.x);
  if (spanned.length === 0) return undefined;
  // Only bend when a straight run would actually hit something. A skip over a gap that happens to be empty
  // at both Y values needs no detour, and adding one would be a kink with nothing to avoid.
  const hits = spanned.some(
    (b) => (source.y > b.y1 && source.y < b.y2) || (target.y > b.y1 && target.y < b.y2),
  );
  if (!hits) return undefined;

  const gap = (RANK_GAP - NODE_WIDTH) / 2;
  const outX = source.x + gap;
  const inX = target.x - gap;
  const clearY = Math.max(...spanned.map((b) => b.y2)) + LANE_CLEARANCE / 2;
  return [
    [source.x, source.y],
    [outX, source.y],
    [outX, clearY],
    [inX, clearY],
    [inX, target.y],
    [target.x, target.y],
  ];
}

/** An SVG path through `points` with rounded corners, so a detour reads as a route and not as a staircase. */
export function roundedPath(points: [number, number][], radius = 8): string {
  if (points.length < 2) return '';
  const parts = [`M${points[0][0]} ${points[0][1]}`];
  for (let i = 1; i < points.length - 1; i += 1) {
    const [px, py] = points[i - 1];
    const [cx, cy] = points[i];
    const [nx, ny] = points[i + 1];
    const r1 = Math.min(radius, Math.hypot(cx - px, cy - py) / 2);
    const r2 = Math.min(radius, Math.hypot(nx - cx, ny - cy) / 2);
    const a: [number, number] = [cx - Math.sign(cx - px) * r1, cy - Math.sign(cy - py) * r1];
    const b: [number, number] = [cx + Math.sign(nx - cx) * r2, cy + Math.sign(ny - cy) * r2];
    parts.push(`L${a[0]} ${a[1]}`, `Q${cx} ${cy} ${b[0]} ${b[1]}`);
  }
  const last = points[points.length - 1];
  parts.push(`L${last[0]} ${last[1]}`);
  return parts.join(' ');
}

const RoutedEdgeComponent: React.FC<EdgeProps> = ({
  id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, markerEnd, style, data,
}) => {
  const d = data as { centerX?: number; points?: [number, number][] } | undefined;
  // A SKIP edge carries its own waypoints, because no combination of `centerX` / `centerY` can put the
  // horizontal run outside the band the cards occupy (see `skipEdgePoints`).
  if (d?.points && d.points.length > 2) {
    return <BaseEdge id={id} path={roundedPath(d.points)} markerEnd={markerEnd} style={style} />;
  }
  const [path] = getSmoothStepPath({
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition,
    ...(typeof d?.centerX === 'number' ? { centerX: d.centerX } : {}),
  });
  return <BaseEdge id={id} path={path} markerEnd={markerEnd} style={style} />;
};

const edgeTypes = { routedEdge: RoutedEdgeComponent };

/**
 * Where the vertical run of a multi-rank edge belongs: the middle of the gap right after the SOURCE.
 *
 * `undefined` for a single-rank edge — its midpoint is already a gap, and moving it would bend a line that
 * had nothing to avoid. Exported so the choice is asserted by measurement.
 */
export function routedEdgeCenterX(sourceX: number, targetX: number): number | undefined {
  if (targetX - sourceX <= RANK_GAP + 1) return undefined;
  return sourceX + NODE_WIDTH + (RANK_GAP - NODE_WIDTH) / 2;
}

/*
 * RETIRED 2026-09-10: `BranchEdgeComponent` / `edgeTypes = { branchEdge: … }`.
 *
 * It existed for ONE reason — React Flow centres a built-in edge's label on the path, and there is no
 * offset prop, so the label had to move into `EdgeLabelRenderer` to be positioned by `branchLabelSlot`.
 * With the label now a row on the source node (`flowNodeOutputs`) an edge carries no label at all, so a
 * custom edge would be a copy of `smoothstep` that draws the identical path. Edges are plain
 * `smoothstep` again.
 *
 * Recorded rather than silently deleted, because the geometry it encoded was correct and the assertions
 * that pinned it (`type: 'branchEdge'`, `<BaseEdge id={id} path={path}`, the opaque-chip properties in
 * `flowViewerGeometry.test.ts`) are being retired in the same commit. A reader who finds those gone from
 * the tests should find the reason here.
 */

function FlowControls() {
  const { t } = useTranslation();
  const { fitView, zoomIn, zoomOut } = useReactFlow();
  const actions = [
    { label: t('common:actions.zoomIn'), icon: <AddIcon fontSize="small" />, run: () => zoomIn() },
    { label: t('common:actions.zoomOut'), icon: <RemoveIcon fontSize="small" />, run: () => zoomOut() },
    { label: t('common:actions.fitView'), icon: <FitScreenIcon fontSize="small" />, run: () => fitView(FIT_VIEW_OPTIONS) },
  ];
  return (
    <Controls showZoom={false} showFitView={false} showInteractive={false}>
      {actions.map((action) => (
        <ControlButton key={action.label} onClick={() => void action.run()} title={action.label} aria-label={action.label}>
          {action.icon}
        </ControlButton>
      ))}
    </Controls>
  );
}

function localizedNodeType(nodeType: string, t: (key: string, options?: any) => string): string {
  const known = ['start', 'approval', 'condition', 'notification', 'cc', 'end'];
  return known.includes(nodeType)
    ? t(`processes:instanceDetail.flowNodeType.${nodeType}`)
    : (nodeType || t('processes:instanceDetail.flowNodeType.other'));
}

function localizedBranchLabel(label: string, t: (key: string, options?: any) => string): string {
  const normalized = label.toLowerCase().trim();
  if (['approved', 'approve'].includes(normalized)) return t('processes:instanceDetail.flowBranch.approved');
  if (['rejected', 'reject'].includes(normalized)) return t('processes:instanceDetail.flowBranch.rejected');
  if (['yes', 'true'].includes(normalized)) return t('processes:instanceDetail.flowBranch.yes');
  if (['no', 'false'].includes(normalized)) return t('processes:instanceDetail.flowBranch.no');
  return label;
}

function resolverDescription(config: Record<string, any>, t: (key: string, options?: any) => string): string {
  const resolver = config.resolver ?? {};
  if (resolver.kind === 'manager_chain') {
    const level = Number(resolver.level ?? 1);
    return level === 1
      ? t('processes:instanceDetail.flowResolver.directManager')
      : t('processes:instanceDetail.flowResolver.managerLevel', { level });
  }
  if (resolver.kind === 'role') {
    return t('processes:instanceDetail.flowResolver.role', { role: resolver.role_code ?? '—' });
  }
  if (resolver.kind === 'specific_worker') return t('processes:instanceDetail.flowResolver.specific');
  if (config.approver_role === 'manager') return t('processes:instanceDetail.flowResolver.directManager');
  if (config.approver_role) return t('processes:instanceDetail.flowResolver.role', { role: config.approver_role });
  return t('processes:instanceDetail.flowResolver.manager');
}

function FlowViewerInner({ flow, currentNodeId, status, timeline = [], currentAssignee, approverCandidates, resolveNodeLabel }: FlowViewerProps) {
  const { t, i18n } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [instance, setInstance] = useState<ReactFlowInstance | null>(null);
  const lastEnteredId = [...(timeline ?? [])].reverse().find(
    (event) => event.action === 'node_entered' && event.node_id,
  )?.node_id ?? null;
  const defaultSelectedId = currentNodeId ?? lastEnteredId ?? flow?.nodes?.[0]?.id ?? null;
  const [selectedId, setSelectedId] = useState<string | null>(defaultSelectedId);

  useEffect(() => {
    setSelectedId(defaultSelectedId);
  }, [defaultSelectedId]);

  const selectNode = useCallback((id: string) => setSelectedId(id), []);
  const model = useMemo(() => {
    const sourceNodes = flow?.nodes ?? [];
    const sourceEdges = flow?.edges ?? [];
    const layout = layoutFlowHorizontally(flow);
    const timelineEvents = timeline ?? [];
    const enteredEvents = timelineEvents
      .map((event, index) => ({ event, index }))
      .filter(({ event }) => event.action === 'node_entered' && event.node_id);
    const enteredPath = enteredEvents.map(({ event }) => event.node_id as string);
    const visited = new Set(enteredPath);
    const negativeEvent = [...timelineEvents].reverse().find(
      (event) => event.node_id && ['rejected', 'cancelled'].includes(event.outcome ?? ''),
    );
    const stoppedNodeId = negativeEvent?.node_id ??
      (['rejected', 'cancelled', 'failed'].includes(status.toLowerCase()) ? currentNodeId : null);
    const traversedTransitions = enteredEvents.slice(1).map((entered, index) => {
      const previous = enteredEvents[index];
      const source = previous.event.node_id as string;
      const target = entered.event.node_id as string;
      const outcome = timelineEvents
        .slice(previous.index + 1, entered.index)
        .reverse()
        .find((event) => event.node_id === source && event.outcome)?.outcome ?? null;
      return { source, target, outcome };
    });
    const incoming = new Set(sourceEdges.map((edge) => edge.target));
    const stateLabel: Record<FlowNodeState, string> = {
      completed: t('processes:instanceDetail.flowState.completed'),
      current: t('processes:instanceDetail.flowState.current'),
      stopped: t('processes:instanceDetail.flowState.stopped'),
      notReached: t('processes:instanceDetail.flowState.notReached'),
    };

    const nodes: Node<ViewerNodeData>[] = sourceNodes.map((node) => {
      const state = deriveFlowNodeState(node.id, visited, currentNodeId, stoppedNodeId);
      // Localized HERE, not in the node component: `flowNodeOutputs` is pure geometry-facing and the
      // layout calls it too, so it must not depend on `t`.
      const outputs = flowNodeOutputs(sourceEdges, node.id).map((output) => ({
        key: output.key,
        name: output.name ? localizedBranchLabel(output.name, t) : null,
      }));
      return {
        id: node.id,
        type: 'flowNode',
        position: layout.positions.get(node.id) ?? { x: 0, y: 0 },
        data: {
          label: flowNodeLabel(node, resolveNodeLabel),
          nodeType: node.type,
          state,
          stateLabel: stateLabel[state],
          icon: ICONS[node.type] ?? null,
          selected: node.id === selectedId,
          hasIncoming: incoming.has(node.id),
          outputs,
          onSelect: () => selectNode(node.id),
        },
        draggable: false,
        connectable: false,
        selectable: true,
      };
    });
    const normalizeOutcome = (value?: string | null): string => {
      const normalized = (value ?? '').toLowerCase().trim();
      if (['approved', 'approve', 'yes', 'true'].includes(normalized)) return 'approved';
      if (['rejected', 'reject', 'no', 'false'].includes(normalized)) return 'rejected';
      return normalized;
    };
    // Node boxes are no longer built here: nothing on the canvas needs collision-testing since the
    // branch labels became node rows. `layoutFlowHorizontally` owns heights now (see `flowNodeHeight`).
    // EVERY CARD'S BOX, from the same layout the nodes render from, so an edge's detour is derived from
    // where the cards actually are rather than from an assumption about lanes.
    const allBoxes = sourceNodes.map((node) => {
      const pos = layout.positions.get(node.id) ?? { x: 0, y: 0 };
      return {
        id: node.id,
        x1: pos.x,
        x2: pos.x + NODE_WIDTH,
        y1: pos.y,
        y2: pos.y + (layout.heights.get(node.id) ?? NODE_HEADER_HEIGHT),
      };
    });
    // The Y an edge LEAVES from: its own named output row, or the header centre for an unnamed exit — the
    // same precedence `FlowNodeComponent` places its handles with.
    const edgeSourceOffset = (edge: FlowEdge): number => {
      const outs = flowNodeOutputs(sourceEdges, edge.source);
      const key = flowOutputKey(edge);
      const index = outs.findIndex((o) => o.key === key);
      return index >= 0 ? outputHandleOffsets(outs)[index] : NODE_HEADER_HEIGHT / 2;
    };

    const edges: Edge[] = sourceEdges.map((edge) => {
      const edgeOutcome = normalizeOutcome(flowOutputName(edge));
      const traversed = traversedTransitions.some((transition) =>
        transition.source === edge.source &&
        transition.target === edge.target &&
        (!transition.outcome || normalizeOutcome(transition.outcome) === edgeOutcome),
      );
      const color = traversed ? '#2e7d32' : '#9e9e9e';
      const from = layout.positions.get(edge.source) ?? { x: 0, y: 0 };
      const to = layout.positions.get(edge.target) ?? { x: 0, y: 0 };
      return {
        id: edge.id,
        source: edge.source,
        target: edge.target,
        // The output's KEY, not the raw `sourceHandle`: a snapshot that names its branch only in
        // `data.on` / `label` has no authored handle, and without promoting the name to one both
        // branches would attach to the node's first handle and leave from the same row
        // (`flowOutputKey`). An authored `sourceHandle` is returned unchanged, so nothing else moves.
        sourceHandle: flowOutputKey(edge) ?? undefined,
        // OUR edge, for ROUTING (see `RoutedEdgeComponent`): the built-in puts its vertical run at the
        // midpoint X, which for a two-rank edge is the intervening node's box. The path itself is still
        // `getSmoothStepPath` — only where it turns is ours.
        type: 'routedEdge',
        // The waypoints are computed HERE, where the layout is; the edge component receives only two
        // endpoints and has no idea what lies between them.
        data: {
          centerX: routedEdgeCenterX(from.x, to.x),
          points: skipEdgePoints(
            { x: from.x + NODE_WIDTH, y: from.y + edgeSourceOffset(edge) },
            { x: to.x, y: to.y + NODE_HEADER_HEIGHT / 2 },
            allBoxes.filter((b) => b.id !== edge.source && b.id !== edge.target),
          ),
        },
        markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12, color },
        style: { stroke: color, strokeWidth: traversed ? 2.5 : 1.5 },
      };
    });
    return { nodes, edges, height: layout.height, sourceNodes, sourceEdges, stateLabel };
  }, [flow, currentNodeId, selectedId, selectNode, status, t, timeline, resolveNodeLabel]);

  useEffect(() => {
    if (!instance || model.nodes.length === 0) return undefined;
    const fit = () => void instance.fitView(FIT_VIEW_OPTIONS);
    const timer = window.setTimeout(fit, 0);
    const observer = typeof ResizeObserver === 'undefined' || !containerRef.current
      ? null
      : new ResizeObserver(fit);
    if (observer && containerRef.current) observer.observe(containerRef.current);
    return () => { window.clearTimeout(timer); observer?.disconnect(); };
  }, [instance, model.nodes.length, model.height]);

  if (model.nodes.length === 0) return null;
  const selected = model.sourceNodes.find((node) => node.id === selectedId) ?? model.sourceNodes[0];
  const selectedData = model.nodes.find((node) => node.id === selected?.id)?.data;
  const config = selected?.data?.config ?? {};
  const selectedTargetHours = approvalTargetHours(config);
  /**
   * The seat's people, used only when no single person is resolved. `currentAssignee` still wins: a
   * `manager_chain` or `specific_worker` step resolves to one person, and naming them is more precise
   * than listing a pool. This fills the case that previously had no answer at all.
   */
  const seatHolders = currentAssignee ? [] : (approverCandidates ?? []);
  const incomingBranches = model.sourceEdges
    .filter((edge) => edge.target === selected?.id)
    .map((edge) => edge.label ?? edge.sourceHandle ?? edge.data?.on)
    .filter(Boolean)
    .map((label) => localizedBranchLabel(String(label), t)) as string[];

  return (
    <Stack spacing={1.25} sx={{ width: '100%' }}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {t('processes:instanceDetail.flowOverview')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('processes:instanceDetail.flowOverviewHint')}
        </Typography>
      </Box>
      <Box
        ref={containerRef}
        role="region"
        aria-label={t('processes:instanceDetail.flowAria')}
        sx={{
          width: '100%',
          height: model.height,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <ReactFlow
          nodes={model.nodes}
          edges={model.edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onInit={setInstance}
          fitView
          fitViewOptions={FIT_VIEW_OPTIONS}
          nodesDraggable={false}
          nodesConnectable={false}
          // `boolean | number[]`, verified against
          // `@reactflow/core/dist/esm/types/component-props.d.ts:92` rather than assumed. The array is the
          // set of mouse BUTTONS that pan: left, middle, right. Plain `panOnDrag` is left-only, and a drag
          // that begins on a NODE is consumed by the node wrapper and never reaches the pane — with the
          // nodes occupying the middle of the canvas, grabbing one and getting nothing is the likely shape
          // of the owner's "不能拖动" (2026-09-08). Middle and right now pan from anywhere.
          //
          // Nodes stay undraggable on purpose: this is a read-only view of a flow SNAPSHOT, so moving a
          // node would imply an edit that has nowhere to persist.
          panOnDrag={[0, 1, 2]}
          zoomOnScroll
          zoomOnPinch
          zoomOnDoubleClick={false}
          minZoom={0.65}
          maxZoom={1.8}
          proOptions={{ hideAttribution: true }}
          style={{ background: '#fafafa' }}
        >
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#e0e0e0" />
          <FlowControls />
        </ReactFlow>
      </Box>

      {/* A `start` or `end` node has no approver, condition, SLA or branch, so this panel could only
          repeat the node's own label and state and add `Type: End` — which on a completed request reads
          as if something were still actionable (owner, 2026-09-09). See `flowNodeHasDetails`. */}
      {selected && selectedData && flowNodeHasDetails(selected.type) && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
              {flowNodeLabel(selected, resolveNodeLabel)}
            </Typography>
            <Chip
              label={selectedData.stateLabel}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.68rem', color: STATE_COLOR[selectedData.state] }}
            />
          </Stack>
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
            <Detail label={t('processes:instanceDetail.flowField.type')} value={localizedNodeType(selected.type, t)} />
            {selected.type === 'approval' && (
              <>
                <Detail
                  label={t('processes:instanceDetail.flowField.approver')}
                  value={selected.id === currentNodeId && currentAssignee
                    ? currentAssignee.name
                    : selected.id === currentNodeId && seatHolders.length > 0
                      ? seatHolders.map((candidate) => candidate.name).join(' · ')
                      : resolverDescription(config, t)}
                />
                {/* The routing RULE as its own row whenever a name is shown above it: "waiting on Admin
                    User" answers who to chase, `Role: Process Owner` answers why them — and if the seat is
                    re-staffed tomorrow, the second row is what explains the change. */}
                {selected.id === currentNodeId && seatHolders.length > 0 && (
                  <Detail
                    label={t('processes:instanceDetail.flowField.routingRule')}
                    value={resolverDescription(config, t)}
                  />
                )}
                {selected.id === currentNodeId && currentAssignee?.email && (
                  <Detail
                    label={t('processes:instanceDetail.flowField.email')}
                    value={currentAssignee.email}
                  />
                )}
                {selected.id === currentNodeId && currentAssignee && (
                  <Detail
                    label={t('processes:instanceDetail.flowField.routingRule')}
                    value={resolverDescription(config, t)}
                  />
                )}
              </>
            )}
            {selectedTargetHours != null && (
              <Detail
                label={t('processes:instanceDetail.flowField.approvalTarget')}
                value={formatApprovalTargetHours(
                  selectedTargetHours,
                  i18n.resolvedLanguage ?? i18n.language,
                )}
              />
            )}
            {config.condition && (
              <Detail label={t('processes:instanceDetail.flowField.condition')} value={String(config.condition)} />
            )}
            {incomingBranches.length > 0 && (
              <Detail label={t('processes:instanceDetail.flowField.branch')} value={incomingBranches.join(' · ')} />
            )}
          </Box>
        </Box>
      )}
    </Stack>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{value || '—'}</Typography>
    </Box>
  );
}

export default function FlowViewer(props: FlowViewerProps) {
  return (
    <ReactFlowProvider>
      <FlowViewerInner {...props} />
    </ReactFlowProvider>
  );
}
